import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faCopy, faEye, faEyeSlash, faKey, faLightbulb,
  faRotate, faExclamationTriangle, faCode, faShieldAlt, faPlus, faLock,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { UiPageHeader, UiButton } from '../components/ui';

interface RevealedCreds {
  api_key: string;
  api_secret: string;
}

const ApiKeysPage: React.FC = () => {
  const { t } = useTranslation();
  const { merchant: authMerchant } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [revealed, setRevealed] = useState<RevealedCreds | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const isEmployee =
    (authMerchant as any)?.role === 'employee' ||
    (authMerchant as any)?.type === 'employee';

  const loadProfile = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/profile');
      setProfile(response.data.data);
    } catch (error) {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const hasKey = !!profile?.api_key;

  const generateKeys = async () => {
    if (isEmployee) {
      toast.error(t('apikeys.employeeBlocked'));
      return;
    }

    const confirmMsg = hasKey
      ? t('apikeys.regenerateConfirm')
      : t('apikeys.generateConfirm');
    if (!window.confirm(confirmMsg)) return;

    try {
      setBusy(true);
      const res = await merchantApi.post('/merchants/regenerate-keys');
      const data = res.data?.data;
      if (data?.api_key && data?.api_secret) {
        setRevealed({ api_key: data.api_key, api_secret: data.api_secret });
        toast.success(
          hasKey ? t('apikeys.regenerateSuccess') : t('apikeys.generateSuccess')
        );
      } else {
        toast.success(t('apikeys.regenerateSuccess'));
      }
      await loadProfile();
    } catch (error: any) {
      const msg = error?.response?.data?.message || t('common.error');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(t('common.copied'));
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.cannotCopy'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <UiPageHeader icon={faKey} title="API Keys" subtitle={t('apikeys.desc')} />

      {/* Warning */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6 flex items-start gap-3">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 text-xl mt-0.5" />
        <div>
          <strong className="text-amber-800 dark:text-amber-300">{t('apikeys.important')}</strong>
          <span className="text-amber-700 dark:text-amber-400 ml-1">{t('apikeys.doNotShare')}</span>
        </div>
      </div>

      {/* Employee block notice */}
      {isEmployee && (
        <div className="bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-6 flex items-start gap-3">
          <FontAwesomeIcon icon={faLock} className="text-gray-500 dark:text-slate-400 text-xl mt-0.5" />
          <div className="text-gray-700 dark:text-slate-300 text-sm">{t('apikeys.employeeBlocked')}</div>
        </div>
      )}

      {/* One-time reveal banner */}
      {revealed && (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-800 rounded-xl p-5 mb-6">
          <h3 className="text-green-800 dark:text-green-300 font-bold text-lg flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faCheckCircle} />
            {t('apikeys.newKeysSuccess')}
          </h3>
          <p className="text-green-700 dark:text-green-400 text-sm mb-4">{t('apikeys.newKeysShown')}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Revealed API Key */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                API Key
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 flex items-center justify-between gap-2">
                <code className="text-xs font-mono text-gray-800 dark:text-slate-100 break-all flex-1">
                  {revealed.api_key}
                </code>
                <button
                  onClick={() => copyToClipboard(revealed.api_key)}
                  className="px-2.5 py-1.5 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-medium flex items-center gap-1.5 flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faCopy} /> {t('common.copy')}
                </button>
              </div>
            </div>

            {/* Revealed API Secret */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                API Secret
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 flex items-center justify-between gap-2">
                <code className="text-xs font-mono text-gray-800 dark:text-slate-100 break-all flex-1">
                  {revealed.api_secret}
                </code>
                <button
                  onClick={() => copyToClipboard(revealed.api_secret)}
                  className="px-2.5 py-1.5 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded-lg text-xs font-medium flex items-center gap-1.5 flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faCopy} /> {t('common.copy')}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-amber-700 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{t('apikeys.oneTimeWarning')}</span>
          </div>
        </div>
      )}

      {/* Empty state (no key yet, and not just revealed) */}
      {!hasKey && !revealed && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-8 border-2 border-dashed border-gray-200 dark:border-slate-700 mb-6 text-center">
          <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faKey} className="text-2xl text-blue-500 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-2">
            {t('apikeys.notYetGenerated')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 max-w-md mx-auto mb-6">
            {t('apikeys.notYetGeneratedDesc')}
          </p>
          <div className="flex justify-center">
            <UiButton
              icon={faPlus}
              onClick={generateKeys}
              loading={busy}
              disabled={isEmployee}
            >
              {t('apikeys.generate')}
            </UiButton>
          </div>
        </div>
      )}

      {/* Current API Keys (when already exists) */}
      {hasKey && profile && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* API Key */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 border-l-4 border-blue-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <FontAwesomeIcon icon={faKey} />
              </div>
              <h3 className="font-bold text-gray-800 dark:text-slate-100">API Key</h3>
            </div>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 flex items-center justify-between gap-3 mb-3">
              <code className="text-sm font-mono text-gray-800 dark:text-slate-100 break-all flex-1">{profile.api_key}</code>
              <button
                onClick={() => copyToClipboard(profile.api_key)}
                className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 flex-shrink-0"
              >
                <FontAwesomeIcon icon={faCopy} /> {t('common.copy')}
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('apikeys.usedInHeader')} <code className="bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-700 dark:text-slate-200">X-API-Key</code>
            </p>
          </div>

          {/* API Secret (masked unless just revealed above) */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 border-l-4 border-purple-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <h3 className="font-bold text-gray-800 dark:text-slate-100">API Secret</h3>
            </div>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 flex items-center justify-between gap-3 mb-3">
              <code className="text-sm font-mono text-gray-800 dark:text-slate-100 flex-1">
                {showSecret ? 'sk_**********************' : '••••••••••••••••••••'}
              </code>
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="px-3 py-2 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 flex-shrink-0"
              >
                <FontAwesomeIcon icon={showSecret ? faEyeSlash : faEye} />
                {showSecret ? t('apikeys.hide') : t('apikeys.show')}
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-2">
              {t('apikeys.usedInHeader')} <code className="bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-700 dark:text-slate-200">X-API-Secret</code>
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {t('apikeys.secretNotShown')}
            </p>
          </div>
        </div>
      )}

      {/* Regenerate Button (only when key exists) */}
      {hasKey && (
        <div className="mb-6">
          <UiButton
            variant="danger"
            icon={faRotate}
            onClick={generateKeys}
            loading={busy}
            disabled={isEmployee}
          >
            {t('apikeys.regenerate')}
          </UiButton>
        </div>
      )}

      {/* Usage Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500" />
          {t('apikeys.howToUse')}
        </h2>

        <div className="bg-gray-900 dark:bg-slate-950 rounded-xl p-5 overflow-x-auto">
          <div className="flex items-center gap-2 text-gray-400 dark:text-slate-500 text-sm mb-3">
            <FontAwesomeIcon icon={faCode} />
            <span>Example Request</span>
          </div>
          <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">{`curl -X POST http://localhost:5000/api/v1/orders \\
  -H "X-API-Key: ${profile?.api_key || 'your_api_key'}" \\
  -H "X-API-Secret: your_api_secret" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 100.00,
    "customer_name": "นาย A"
  }'`}</pre>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysPage;
